import { ExportOutlined } from "@ant-design/icons";
import { InfoTooltip } from "@impero-com/common/components/infoTooltip/tooltip";
import { MenuItemSpec } from "@impero-com/common/components/leftSideMenu";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Space } from "antd";

import { LegacyRoute } from "../../../legacyRoutes";
import { WebRoute } from "../../../routes";
import { Report } from "../types";

import { ReportListPageType } from "./layout";
import * as theme from "./theme.css";
import { buildReportUrl } from "./urlUtils";

const NAV_SAVED_REPORTS_ID = "saved-reports";
const NAV_SAVED_REPORTS_ALL_ID = "saved-reports-all";
const NAV_SAVED_REPORTS_STATUS_ID = "saved-reports-status";
const NAV_SAVED_REPORTS_CLASSIFICATION_ID = "saved-reports-classification";
const NAV_SAVED_REPORTS_WORKLOAD_ID = "saved-reports-workload";
const NAV_SAVED_REPORTS_DATASHEET_ID = "saved-reports-datasheet";
const NAV_STANDARD_REPORTS_ID = "standard-reports";
const NAV_STANDARD_REPORTS_STATUS_ID = "standard-reports-status";
const NAV_STANDARD_REPORTS_WORKLOAD_ID = "standard-reports-workload";

type NavigationItemId =
    | typeof NAV_SAVED_REPORTS_ID
    | typeof NAV_SAVED_REPORTS_ALL_ID
    | typeof NAV_SAVED_REPORTS_STATUS_ID
    | typeof NAV_SAVED_REPORTS_CLASSIFICATION_ID
    | typeof NAV_SAVED_REPORTS_WORKLOAD_ID
    | typeof NAV_SAVED_REPORTS_DATASHEET_ID
    | typeof NAV_STANDARD_REPORTS_ID
    | typeof NAV_STANDARD_REPORTS_STATUS_ID
    | typeof NAV_STANDARD_REPORTS_WORKLOAD_ID;

function buildNavigationItems(
    reportsCount: ReportsCount,
    pageType: ReportListPageType
): Array<MenuItemSpec> {
    return [
        {
            type: "SubMenuItem",
            id: NAV_SAVED_REPORTS_ID,
            label: (
                <Space>
                    {t("Report_Navigation_SavedReports")}
                    <InfoTooltip
                        text={t("Report_Navigation_SavedReports_Info")}
                        iconClassName={theme.navigationInfoIcon}
                    />
                </Space>
            ),
            childItems: [
                {
                    type: "SimpleMenuItem",
                    id: NAV_SAVED_REPORTS_ALL_ID,
                    label: t("Report_Navigation_SavedReports_All", {
                        count: reportsCount.all,
                    }),
                    handler: { type: "ROUTER", linkTo: WebRoute.REPORTS },
                },
                {
                    type: "SimpleMenuItem",
                    id: NAV_SAVED_REPORTS_STATUS_ID,
                    label: t("Report_Navigation_SavedReports_Status", {
                        count: reportsCount.status,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: WebRoute.REPORTS_STATUS,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: NAV_SAVED_REPORTS_CLASSIFICATION_ID,
                    label: t("Report_Navigation_SavedReports_Classification", {
                        count: reportsCount.classification,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: WebRoute.REPORTS_CLASSIFICATION,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: NAV_SAVED_REPORTS_WORKLOAD_ID,
                    label: t("Report_Navigation_SavedReports_Workload", {
                        count: reportsCount.workload,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: WebRoute.REPORTS_WORKLOAD,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: NAV_SAVED_REPORTS_DATASHEET_ID,
                    label: t("Report_Navigation_SavedReports_Datasheet", {
                        count: reportsCount.datasheet,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: WebRoute.REPORTS_DATASHEET,
                    },
                },
            ],
        },
        {
            type: "SubMenuItem",
            id: NAV_STANDARD_REPORTS_ID,
            label: (
                <Space>
                    {t("Report_Navigation_StandardReports")}
                    <InfoTooltip
                        text={t("Report_Navigation_StandardReports_Info")}
                        iconClassName={theme.navigationInfoIcon}
                    />
                </Space>
            ),
            childItems: [
                {
                    type: "SimpleMenuItem",
                    id: NAV_STANDARD_REPORTS_STATUS_ID,
                    label: (
                        <Space>
                            {t("Report_Navigation_StandardReports_Status")}
                            <ExportOutlined />
                        </Space>
                    ),
                    handler: {
                        type: "HTML",
                        href: buildReportUrl(
                            LegacyRoute.REPORT_CONTROL_RESULT_STATUS,
                            pageType
                        ),
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: NAV_STANDARD_REPORTS_WORKLOAD_ID,
                    label: (
                        <Space>
                            {t("Report_Navigation_StandardReports_Workload")}
                            <ExportOutlined />
                        </Space>
                    ),
                    handler: {
                        type: "HTML",
                        href: buildReportUrl(
                            LegacyRoute.REPORT_CONTROL_RESULT_STATUS,
                            pageType,
                            { workload: "" }
                        ),
                    },
                },
            ],
        },
    ];
}

interface ReportsCount {
    all: number;
    status: number;
    classification: number;
    workload: number;
    datasheet: number;
}

function countReports(reports: Array<Report>): ReportsCount {
    let status = 0;
    let classification = 0;
    let workload = 0;
    let datasheet = 0;

    for (const report of reports) {
        switch (report.type) {
            case "Status":
                status += 1;
                break;
            case "Classification":
                classification += 1;
                break;
            case "Workload":
                workload += 1;
                break;
            case "Datasheet":
                datasheet += 1;
                break;
            default:
                return assertNever(report);
        }
    }

    return {
        all: reports.length,
        status,
        classification,
        workload,
        datasheet,
    };
}

function pageTypeToNavItemId(pageType: ReportListPageType): NavigationItemId {
    switch (pageType) {
        case "All":
            return NAV_SAVED_REPORTS_ALL_ID;
        case "Status":
            return NAV_SAVED_REPORTS_STATUS_ID;
        case "Classification":
            return NAV_SAVED_REPORTS_CLASSIFICATION_ID;
        case "Workload":
            return NAV_SAVED_REPORTS_WORKLOAD_ID;
        case "Datasheet":
            return NAV_SAVED_REPORTS_DATASHEET_ID;
        default:
            return assertNever(pageType);
    }
}

export { buildNavigationItems, countReports, pageTypeToNavItemId };

export type { ReportsCount };
